import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CspConfig, getCspConfigId } from 'data/cspConfig/cspConfig.types';
import * as cspConfigSelectors from 'data/cspConfig/cspConfig.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/cspConfig/cspConfig.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import { FormTemplateSection } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import CspConfigForm, { CspConfigFormRef } from '../../components/CspConfigForm';
import { cspConfigTemplate } from '../../templates/cspConfigTemplate';
import getCspConfigDataToSubmit from '../../utils/getCspConfigDataToSubmit';
import getContactOptions from '../../utils/getContactOptions';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedCspConfig: cspConfigSelectors.getSelectedCspConfig(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  cspConfig: cspConfigSelectors.getCspConfig(state),
  contacts: contactSelectors.getContacts(state),
});

const mapDispatch = {
  editCspConfig: actions.updateCspConfig,
  goToCspConfigList: actions.goToCspConfigList,
  getCspConfig: actions.getCspConfig,
  getContacts: contactActions.getAllContacts,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CspConfigEdit: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedCspConfig,
  history,
  isAuditEnabled,
  userId,
  userName,
  cspConfig,
  contacts,
  editCspConfig,
  goToCspConfigList,
  getCspConfig,
  getContacts,
}: Props) => {
  const [template, setTemplate] = useState(cspConfigTemplate);
  const [formData, setFormData] = useState<Partial<CspConfig>>({});
  const cspConfigFormRef = useRef<CspConfigFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getContacts(deliveryFunctionId);
  }, [deliveryFunctionId, getContacts]);

  useEffect(() => {
    const selected = selectedCspConfig[0];
    const id = getCspConfigId(selected);

    if (id && cspConfig?.cspid !== selected?.cspid) {
      getCspConfig(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedCspConfig, cspConfig, getCspConfig]);

  useEffect(() => {
    if (cspConfig != null) {
      setFormData(cspConfig);

      const clonedTemplate = cloneDeep(cspConfigTemplate);
      clonedTemplate.formId = Date.now().toString();

      // cspid is the key the CSP is stored and addressed under in Xcipio, so it cannot
      // be changed once the CSP exists.
      (clonedTemplate.main as FormTemplateSection).fields.cspid.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.cspid.disabled = true;

      (clonedTemplate.main as FormTemplateSection).fields.legalcontact.options =
        getContactOptions(contacts, cspConfig.legalcontact);
      (clonedTemplate.main as FormTemplateSection).fields.nwcontact.options =
        getContactOptions(contacts, cspConfig.nwcontact);

      setTemplate(clonedTemplate);
    }
  }, [cspConfig, contacts]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    if (cspConfig != null && cspConfig.cspid == null) {
      return;
    }

    const isValid = cspConfigFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = cspConfigFormRef?.current?.getValues();
    if (!formValues || cspConfig == null) {
      return;
    }

    const body = getCspConfigDataToSubmit(
      // cspid is not editable, so take it from the loaded CSP rather than the form.
      { ...formValues, cspid: cspConfig.cspid },
      cspConfig,
      isAuditEnabled,
      userId,
      userName,
      'UPDATE',
    );

    editCspConfig(deliveryFunctionId, getCspConfigId(cspConfig), body, history);
  };

  const handleClickCancel = () => goToCspConfigList(history);

  if (!cspConfig) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
          onClickClose={handleClickCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CspConfigEdit">
      {renderFormPanel(t('cspConfig.edit.title'))}
      <CspConfigForm
        data={formData}
        template={template}
        ref={cspConfigFormRef}
        onFieldUpdate={handleFieldUpdate}
      />
    </div>
  );
};

export default withRouter(connector(CspConfigEdit));
